/**
 * Light or dark chrome, for the whole app.
 *
 * The preference is `system` until someone picks otherwise, and lives in this
 * browser like the language — it is a property of the screen, not of a plan, so
 * it is never written into a document or a backup. What the page actually wears
 * is `data-theme` on <html>, which every colour token in `tokens.css` keys off.
 *
 * `index.html` runs the same resolution inline before the stylesheet paints, so
 * a dark-mode visitor never sees a flash of paper. Keep the two in step.
 */

export type ThemePreference = 'system' | 'light' | 'dark'
export type Theme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'yerbeyer.theme'
export const THEME_ORDER: ThemePreference[] = ['system', 'light', 'dark']

/** The browser bar's colour per theme — `--paper` of each. */
const THEME_COLOR: Record<Theme, string> = { light: '#F4F2EE', dark: '#161719' }

const DARK_QUERY = '(prefers-color-scheme: dark)'

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark'
}

export function storedThemePreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY)
    return isThemePreference(saved) ? saved : 'system'
  } catch {
    return 'system' // private mode: follow the device
  }
}

export function nextThemePreference(current: ThemePreference): ThemePreference {
  return THEME_ORDER[(THEME_ORDER.indexOf(current) + 1) % THEME_ORDER.length]
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean): Theme {
  return preference === 'system' ? (systemDark ? 'dark' : 'light') : preference
}

function systemPrefersDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia(DARK_QUERY).matches
}

function applyTheme(preference: ThemePreference): void {
  if (typeof document === 'undefined') return
  const theme = resolveTheme(preference, systemPrefersDark())
  const root = document.documentElement
  root.dataset.theme = theme
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme])
}

// A tiny external store, read with useSyncExternalStore: the preference changes
// rarely, and nothing but the switch itself needs to re-render when it does —
// everything else follows through CSS.
let preference: ThemePreference = storedThemePreference()
const listeners = new Set<() => void>()

export function getThemePreference(): ThemePreference {
  return preference
}

export function setThemePreference(next: ThemePreference): void {
  preference = next
  try {
    if (next === 'system') localStorage.removeItem(THEME_STORAGE_KEY)
    else localStorage.setItem(THEME_STORAGE_KEY, next)
  } catch {
    /* non-fatal: the choice simply won't survive a reload */
  }
  applyTheme(next)
  listeners.forEach((listener) => listener())
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Applies the stored preference and follows the device while it is `system`. */
export function startTheme(): void {
  applyTheme(preference)
  if (typeof matchMedia !== 'function') return
  matchMedia(DARK_QUERY).addEventListener('change', () => {
    if (preference === 'system') applyTheme('system')
  })
  // Another tab changed it.
  window.addEventListener('storage', (event) => {
    if (event.key !== THEME_STORAGE_KEY) return
    preference = storedThemePreference()
    applyTheme(preference)
    listeners.forEach((listener) => listener())
  })
}
